import { useCallback, useEffect, useMemo, useState } from 'react';

import { getRightPanelRecommendations } from '@atlassian/search-client';

import type { ProductKeys } from '../../common/constants/products';
import {
	onInvalidIdentifiers,
	onRecommendationsCacheAccess,
	onRecommendationsDegraded,
} from '../../common/utils/events/recommendations-events';
import { useAnalytics } from '../analytics';
import { useSearchSessionId } from '../store/bootstrap';

export type UseRightPanelRecommendationsProps = {
	userId?: string | null;
	showRecommendations?: boolean;
};

type CacheAccessState = {
	refreshTriggered: boolean;
	hit: boolean;
};

export function useRightPanelRecommendations({
	userId,
	showRecommendations,
}: UseRightPanelRecommendationsProps): {
	recommended3pApps: string[];
	loading: boolean;
} {
	const { fireAnalyticsEvent } = useAnalytics();
	const [searchSessionId] = useSearchSessionId();

	const [recommended3pApps, setRecommended3pApps] = useState<string[]>([]);
	const [loading, setLoading] = useState(false);

	const analyticsCallback = useCallback(
		async (state: CacheAccessState) => {
			try {
				fireAnalyticsEvent(
					onRecommendationsCacheAccess({
						searchSessionId,
						hit: state.hit,
					}),
				);
			} catch {}
		},
		[fireAnalyticsEvent, searchSessionId],
	);

	const degradationCallback = useCallback(
		(reason: 'timeout' | 'failure') => {
			try {
				fireAnalyticsEvent(onRecommendationsDegraded({ searchSessionId, reason }));
			} catch {}
		},
		[fireAnalyticsEvent, searchSessionId],
	);

	const fetchRecs = useCallback(async () => {
		if (!showRecommendations) {
			setRecommended3pApps([]);
			setLoading(false);
			return;
		} else if (!userId) {
			setRecommended3pApps([]);
			setLoading(false);
			fireAnalyticsEvent(onInvalidIdentifiers({ searchSessionId }));
			return;
		} else {
			setLoading(true);
			try {
				const recs = (await getRightPanelRecommendations({
					userId,
					analyticsCallback,
					degradationCallback,
				})) as ProductKeys[];
				setRecommended3pApps(Array.isArray(recs) ? recs : []);
			} finally {
				setLoading(false);
			}
		}
	}, [
		fireAnalyticsEvent,
		userId,
		searchSessionId,
		showRecommendations,
		analyticsCallback,
		degradationCallback,
	]);

	useEffect(() => {
		void fetchRecs();
	}, [fetchRecs]);

	return useMemo(() => ({ recommended3pApps, loading }), [recommended3pApps, loading]);
}
